import { useEffect, useRef, useCallback } from "react";

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
}

export function useFocusableElements(containerRef: React.RefObject<HTMLDivElement | null>, isVisible: boolean) {
  const focusableElementsRef = useRef<HTMLElement[]>([]);
  const currentFocusIndexRef = useRef<number>(0);
  const firstFocusRef = useRef<boolean>(true);

  const updateFocusableElements = useCallback(() => {

    focusableElementsRef.current = getFocusableElements(containerRef.current);

    if (focusableElementsRef.current.length > 0) {
      currentFocusIndexRef.current = focusableElementsRef.current.indexOf(document.activeElement as HTMLElement);
      if (currentFocusIndexRef.current === -1) currentFocusIndexRef.current = 0;

      // focusableElementsRef.current[0]?.focus();
    } 
  }, [containerRef]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!containerRef.current || focusableElementsRef.current.length === 0) return;

    if (event.key === "Tab") {
      event.preventDefault();
      if (firstFocusRef.current) {
        focusableElementsRef.current[0]?.focus();
        firstFocusRef.current = false;
      } else {
        const direction = event.shiftKey ? -1 : 1;
        currentFocusIndexRef.current =
          (currentFocusIndexRef.current + direction + focusableElementsRef.current.length) %
        focusableElementsRef.current.length;

        focusableElementsRef.current[currentFocusIndexRef.current]?.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!isVisible) return; // ✅ Early return when modal is not visible
  
    document.addEventListener("keydown", handleKeyDown);
  
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isVisible, handleKeyDown]); // ✅ Only runs when `isVisible` changes
  

  return {
    focusableElements: focusableElementsRef.current,
    updateFocusableElements,
  };
}
